import React, { useRef, useState, forwardRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import without from 'lodash/without'
import times from 'lodash/times'
import { classNames, Icon, iconProps, useUpdateRef } from '@zambezi/sdk/common'
import DropdownList from './DropdownList'

/**
 * Create a React Multi Select Dropdown List
 *
 * @template T
 * @template Item
 * @param {import('./interfaces').MultiSelectDropdownListProps<T, Item>} props
 * @param {React.Ref} ref
 * @returns {JSX.Element}
 */
function multiSelectDropdownList (props, ref) {
  const [selectedItemsIndices, setSelectedItemsIndices] = useState(
    props.selectedItemsIndices === undefined
      ? props.defaultSelectedItemsIndices
      : props.selectedItemsIndices
  )
  /** @type {React.RefObject<HTMLElement>} */
  const dropdownListRef = useRef(null)
  useUpdateRef(ref, dropdownListRef, { focus })
  useEffect(onSelectedItemsIndicesChange, [props.selectedItemsIndices])

  const { className, withGrouping } = props

  return (
    <DropdownList
      {...props}
      className={classNames('zb-multiselect-dropdown-list', className)}
      selectedItemsIndices={selectedItemsIndices}
      itemClassName={getItemClassName}
      itemProps={getItemProps}
      listProps={{
        'aria-multiselectable': 'true'
      }}
      renderItem={getRenderItem}
      onItemClick={handleItemClick}
      withGrouping={withGrouping}
      withSpaceSelect
      onGroupLabelClick={handleGroupLabelClick}
      renderGroupLabel={getRenderGroupLabel}
      ref={dropdownListRef}
    />
  )

  /**
   * @param {Item} d
   * @param {number} i
   */
  function getItemClassName (d, i) {
    return classNames({
      'zb-multiselect-dropdown-list-item-is-selected': isItemSelected(i)
    })
  }

  /**
   * @param {Item} d
   * @param {number} i
   */
  function getItemProps (d, i) {
    return {
      'aria-selected': isItemSelected(i)
    }
  }

  /**
   * @param {Item} d
   * @param {number} i
   */
  function getRenderItem (d, i) {
    const { renderItem, iconProps } = props

    return (
      <div>
        <span className='zb-multiselect-dropdown-list-item-icon'>
          <Icon
            name='core-dropdown-item-selected'
            title='Selected Item'
            aria-hidden={false}
            iconProps={iconProps}
          />
        </span>

        {renderItem(d, i, isItemSelected(i))}
      </div>
    )
  }

  /**
   * @param {Item} d
   * @param {number} i
   */
  function getRenderGroupLabel (d, i) {
    const { renderGroupLabel } = props
    const isSelected = getGroupChildrenIndices(i).every(isItemSelected)

    return (
      <div
        className={classNames({
          'zb-multiselect-dropdown-list-group-is-selected': isSelected
        })}
      >
        <span className='zb-multiselect-dropdown-list-group-label-icon'>
          <Icon name='core-dropdown-item-selected' title='Selected Group' />
        </span>

        {renderGroupLabel(d, i)}
      </div>
    )
  }

  /**
   * @param {FocusOptions | number} focusOptions
   */
  function focus (focusOptions) {
    const dropdownList = dropdownListRef.current
    dropdownList && dropdownList.focus(focusOptions)
  }

  function onSelectedItemsIndicesChange () {
    if (!isUncontrolled(props)) {
      setSelectedItemsIndices(props.selectedItemsIndices)
    }
  }

  /**
   * @param {number} i
   */
  function isItemSelected (i) {
    return selectedItemsIndices.indexOf(i) !== -1
  }

  /**
   * @param {number} i
   */
  function isItemDisabled (i) {
    return props.disabledItemsIndices.indexOf(i) !== -1
  }

  /**
   * @param {Item} d
   * @param {number} i
   */
  function handleItemClick (d, i) {
    const isSelected = isItemSelected(i)

    const newSelectedIndices = isSelected
      ? without(selectedItemsIndices, i)
      : selectedItemsIndices.concat(i)

    if (isUncontrolled(props)) {
      setSelectedItemsIndices(newSelectedIndices)
    }

    props.onSelectedItemsChanged(newSelectedIndices)
  }

  /**
   * @param {Item} d
   * @param {number} i
   */
  function handleGroupLabelClick (d, i) {
    const enabledChildrenIndices = getGroupChildrenIndices(i).filter(
      i => !isItemDisabled(i)
    )
    const isSelected = enabledChildrenIndices.every(isItemSelected)

    const newSelectedIndices = isSelected
      ? without(selectedItemsIndices, ...enabledChildrenIndices)
      : selectedItemsIndices
        .concat(enabledChildrenIndices)
        .filter((item, index, indices) => indices.indexOf(item) === index)

    if (isUncontrolled(props)) {
      setSelectedItemsIndices(newSelectedIndices)
    }

    props.onSelectedItemsChanged(newSelectedIndices)
    props.onGroupLabelClick(d, i)
  }

  /**
   * @param {number} i
   * @returns {number[]}
   */
  function getGroupChildrenIndices (i) {
    const { data, getGroupChildren } = props

    const group = data[i]
    const numberOfSiblings = data
      .slice(0, i)
      .reduce((acc, g) => acc + getGroupChildren(g).length, 0)
    const children = getGroupChildren(group)

    return times(children.length, i => i + numberOfSiblings)
  }
}

/**
 * @template T, Item
 * @param {import('./interfaces').MultiSelectDropdownListProps<T, Item>} props
 */
function isUncontrolled (props) {
  return props.selectedItemsIndices === undefined
}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').MultiSelectDropdownListProps<T, Item> & React.RefAttributes<HTMLElement>>}
 */
const MultiSelectDropdownList = forwardRef(multiSelectDropdownList)

MultiSelectDropdownList.displayName = 'MultiSelectDropdownList'

MultiSelectDropdownList.propTypes = {
  data: PropTypes.array,
  defaultSelectedItemsIndices: PropTypes.array,
  selectedItemsIndices: PropTypes.array,
  disabledItemsIndices: PropTypes.array,
  renderItem: PropTypes.func,
  header: PropTypes.element,
  footer: PropTypes.element,
  onSelectedItemsChanged: PropTypes.func,
  arrowPosition: PropTypes.oneOf(['left', 'right']),
  withGrouping: PropTypes.bool,
  renderGroupLabel: PropTypes.func,
  getGroupChildren: PropTypes.func,
  onGroupLabelClick: PropTypes.func,
  iconProps
}

MultiSelectDropdownList.defaultProps = {
  data: [],
  defaultSelectedItemsIndices: [],
  selectedItemsIndices: undefined,
  disabledItemsIndices: [],
  renderItem: String,
  header: undefined,
  footer: undefined,
  onSelectedItemsChanged: () => undefined,
  withGrouping: false,
  renderGroupLabel: String,
  getGroupChildren: () => [],
  onGroupLabelClick: () => {}
}

export default MultiSelectDropdownList
